import cx from 'clsx';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Tooltip } from 'react-tooltip';

import { ReactComponent as TableSort } from '@assets/svg/table-sort.svg';
import { ReactComponent as ThreeDotsIcon } from '@assets/svg/three-dots.svg';

import ButtonGoUp from '@components/common/ButtonGoUp/ButtonGoUp';
import TableEmptyRowExtended from '@components/common/TableEmptyRowExtended/TableEmptyRowExtended';
import TableLoading from '@components/common/TableLoading/TableLoading';
import TableLoadingTrigger from '@components/common/TableLoadingTrigger/TableLoadingTrigger';
import TooltipSort from '@components/common/TooltipSort/TooltipSort';
import Button from '@components/uikit/Button/Button';
import Checkbox from '@components/uikit/Checkbox/Checkbox';

import { MODAL_NAMES } from '@constants/modalRenderers';
import { initialSorting } from '@constants/tableSorting';

import { showToast } from '@helpers/showToast';

import { useDebouncedValue } from '@hooks/useDebouncedValue';
import { useIntersectionObserver } from '@hooks/useIntersectionObserver';
import useTranslate from '@hooks/useTranslate';
import useTranslation from '@hooks/useTranslation';

import { useUserProfileQuery } from '@services/user/useUserProfileQuery';

import { selectAppLocationSelectValue } from '@store/useAppStore';
import { useModalActions } from '@store/useModalStore';

import type { IEquipmentItem, IManageEquipmentItem } from '../../model/equipmentsList';
import type { TEquipmentStatus } from '../../model/equipmentsList';
import { useEquipmentsActivateMutation } from '../../services/useEquipmentsActivateMutation';
import { useEquipmentsListInfiniteQuery } from '../../services/useEquipmentsListInfiniteQuery';
import {
  selectManageEquipmentActiveEquipment,
  selectManageEquipmentInactiveEquipment,
  selectManageEquipmentSearch,
  selectManageEquipmentSelectedAllValue,
  selectManageEquipmentSelectedRows,
  selectManageEquipmentSorting,
} from '../../store/useManageEquipmentStore';
import { useManageEquipmentActions } from '../../store/useManageEquipmentStore';
import ContextMenu from '../ContextMenu/ContextMenu';
import DeactivationPeriodColumn from '../DeactivationPeriodColumn/DeactivationPeriodColumn';
import TableEquipmentsSkeleton from './TableEquipmentsSkeleton';
import style from './style.module.scss';

interface IProps {
  equipmentStatusValue: TEquipmentStatus;
}

const TableEquipments: React.FC<IProps> = ({ equipmentStatusValue }) => {
  const { t } = useTranslation();
  const { translateField } = useTranslate();

  const equipmentTableContainerRef = useRef(null);
  const [totalByStatus, setTotalByStatus] = useState<{ [equipmentStatusValue: string]: number }>({
    all: 0,
    active: 0,
    inactive: 0,
  });

  const usersTimezone = -new Date().getTimezoneOffset() / 60;

  const { setActiveModal, setSecondaryModal } = useModalActions();

  const equipmentSearch = selectManageEquipmentSearch();
  const locationSelectValue = selectAppLocationSelectValue();
  const selectedAll = selectManageEquipmentSelectedAllValue();
  const selectedRows = selectManageEquipmentSelectedRows();
  const activeEquipment: Array<IManageEquipmentItem> = selectManageEquipmentActiveEquipment();
  const inactiveEquipment: Array<IManageEquipmentItem> = selectManageEquipmentInactiveEquipment();
  const sorting = selectManageEquipmentSorting();

  const {
    setEquipmentSelectedAll,
    setEquipmentSelectedRows,
    setActiveEquipment,
    setInactiveEquipment,
    setSorting,
  } = useManageEquipmentActions();

  const [debouncedValue] = useDebouncedValue(equipmentSearch, 500);

  const { mutate: equipmentsActivateMutate } = useEquipmentsActivateMutation();

  const { data: profileData } = useUserProfileQuery();

  const {
    data: equipmentsListData,
    isFetching: equipmentsListIsFetching,
    isLoading: equipmentsListIsLoading,
    hasNextPage: equipmentsListHasNextPage,
    fetchNextPage: equipmentsListFetchNextPage,
  } = useEquipmentsListInfiniteQuery({
    sorting,
    searchValue: debouncedValue,
    locationIds:
      locationSelectValue.level_name === 'allFacilities' ? undefined : locationSelectValue?.filterIds,
    equipmentStatusValue,
  });

  const [infiniteScrollRef, isInViewPort] = useIntersectionObserver({
    threshold: 0.1,
    reInitializeOnChange: equipmentsListIsFetching,
  });

  const isManageEquipmentDeactivation = !!profileData?.userAccess?.manageEquipmentDeactivation;

  const tableData = useMemo(() => {
    if (!equipmentsListData) {
      return [];
    }
    const total = equipmentsListData?.pages[0]['total'];
    setTotalByStatus({ ...totalByStatus, [equipmentStatusValue]: total });

    return equipmentsListData?.pages?.flatMap((page) => page.list) ?? [];
  }, [equipmentsListData]);

  useEffect(() => {
    if (isInViewPort && equipmentsListHasNextPage && !equipmentsListIsFetching) {
      equipmentsListFetchNextPage().then(({ isSuccess }) => {
        if (isSuccess) {
          setEquipmentSelectedAll(false);
        }
      });
    }
  }, [isInViewPort, equipmentsListHasNextPage, equipmentsListIsFetching]);

  const onChangeSorting = (key: string) => {
    if (!sorting.id || sorting.id !== key) {
      setSorting({ id: key, desc: false });
      return;
    }
    const { id, desc } = sorting;
    if (desc) {
      setSorting(initialSorting);
    } else {
      setSorting({ id, desc: true });
    }
  };

  const onSelectAll = () => {
    if (selectedAll) {
      setEquipmentSelectedRows({});
      setEquipmentSelectedAll(false);
      setActiveEquipment([]);
      setInactiveEquipment([]);
    } else {
      const res: { [key: number]: boolean } = {};
      const active: Array<IManageEquipmentItem> = [];
      const inactive: Array<IManageEquipmentItem> = [];

      tableData.forEach((row) => {
        res[row.id] = true;
        const equipment = { id: row.id, name: row.name, facility_offset: usersTimezone };
        if (row.deactivateTable) {
          inactive.push(equipment);
        } else {
          active.push(equipment);
        }
      });

      setEquipmentSelectedRows(res);
      setEquipmentSelectedAll(true);
      setActiveEquipment(active);
      setInactiveEquipment(inactive);
    }
  };

  const onChangeRowCheckbox = (row: IEquipmentItem) => {
    if (selectedRows[row.id]) {
      const rows = { ...selectedRows };
      delete rows[row.id];
      setEquipmentSelectedRows(rows);
      setEquipmentSelectedAll(false);
      if (row.deactivateTable) {
        setInactiveEquipment(inactiveEquipment.filter((eq) => eq.id !== row.id));
      } else {
        setActiveEquipment(activeEquipment.filter((eq) => eq.id !== row.id));
      }
    } else {
      setEquipmentSelectedRows({
        ...selectedRows,
        [row.id]: true,
      });
      const equipment = { id: row.id, name: row.name, facility_offset: usersTimezone };
      if (row.deactivateTable) {
        setInactiveEquipment(inactiveEquipment.concat(equipment));
      } else {
        setActiveEquipment(activeEquipment.concat(equipment));
      }
    }
  };

  const handleActivateEquipment = () => {
    setActiveModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Activate Equipment'),
        bodyContent: t('Are you sure you want to activate the selected equipment?'),
        onConfirm: () => {
          equipmentsActivateMutate(
            { equipments: inactiveEquipment.map((eq) => eq.id) },
            {
              onSuccess: () => {
                showToast({ message: t('Your changes have been saved.'), variant: 'success' });
                if (selectedAll) {
                  setEquipmentSelectedAll(false);
                }
                if (inactiveEquipment.length > 1) {
                  const rows = { ...selectedRows };
                  inactiveEquipment.forEach((eq) => {
                    delete rows[eq.id];
                  });
                  setEquipmentSelectedRows(rows);
                  setInactiveEquipment([]);
                } else {
                  const rows = { ...selectedRows };
                  delete rows[inactiveEquipment[0].id];
                  setEquipmentSelectedRows(rows);
                  setInactiveEquipment(inactiveEquipment.filter((eq) => eq.id !== inactiveEquipment[0].id));
                }
              },
            },
          );
        },
      },
    });
  };

  const handleDeactivateEquipment = () => {
    setSecondaryModal({
      name: MODAL_NAMES.modalConfirm,
      options: {
        bodyTitle: t('Deactivate Equipment'),
        bodyContent: t('Are you sure you want to deactivate the selected equipment?'),
        onConfirm: () => {
          setActiveModal({
            name: MODAL_NAMES.equipmentDeactivateModal,
            options: { selectedEquipments: activeEquipment },
          });
        },
      },
    });
  };

  const handleEditDeactivationPeriodEquipment = () => {
    setActiveModal({
      name: MODAL_NAMES.editEquipmentDeactivationPeriodModal,
      options: { selectedEquipments: inactiveEquipment },
    });
  };

  return (
    <div>
      <div className={style.equipmentTableWrapper} ref={equipmentTableContainerRef}>
        <table className={style.equipmentTable}>
          <thead>
            <tr>
              <th className={style.selectColumn}>
                <Checkbox
                  value={selectedAll}
                  onChange={onSelectAll}
                  disabled={!isManageEquipmentDeactivation}
                  withHelperText={false}
                />
              </th>
              <th className={style.locationColumn}>
                <div className={style.sortColumnWrapper}>
                  <span className={style.ellipsisTextThreeLines}>{t('Location')}</span>
                  <TableSort
                    className={cx(style.equipmentTableSortIcon, {
                      [style.activeSort]: sorting.id === 'location_name',
                    })}
                    onClick={() => onChangeSorting('location_name')}
                    data-tooltip-content="location_name"
                    data-tooltip-id="sort-tooltip"
                  />
                </div>
              </th>
              <th>
                <div className={style.sortColumnWrapper}>
                  <span className={style.ellipsisTextThreeLines}>{t('Equipment')}</span>
                  <TableSort
                    className={cx(style.equipmentTableSortIcon, {
                      [style.activeSort]: sorting.id === 'name',
                    })}
                    onClick={() => onChangeSorting('name')}
                    data-tooltip-content="name"
                    data-tooltip-id="sort-tooltip"
                  />
                </div>
              </th>
              <th className={style.assetCodeColumn}>
                <div className={style.sortColumnWrapper}>
                  <span className={style.ellipsisTextThreeLines}>{t('Asset Code')}</span>
                  <TableSort
                    className={cx(style.equipmentTableSortIcon, {
                      [style.activeSort]: sorting.id === 'asset_code',
                    })}
                    onClick={() => onChangeSorting('asset_code')}
                    data-tooltip-content="asset_code"
                    data-tooltip-id="sort-tooltip"
                  />
                </div>
              </th>
              <th className={style.equipmentTypeColumn}>
                <span className={cx(style.ellipsisTextTwoLines, style.bigWidthShow)}>
                  {t('Equipment Type')}
                </span>
                <span className={style.smallWidthShow}>{t('Type')}</span>
              </th>
              <th className={style.countSensorsColumn}>
                <div className={style.sortColumnWrapper}>
                  <span className={style.ellipsisTextThreeLines}>{t('Installed sensors')}</span>
                  <TableSort
                    className={cx(style.equipmentTableSortIcon, {
                      [style.activeSort]: sorting.id === 'count_sensors',
                    })}
                    onClick={() => onChangeSorting('count_sensors')}
                    data-tooltip-content="count_sensors"
                    data-tooltip-id="sort-tooltip"
                  />
                </div>
              </th>
              <th>{t('Status')}</th>
              <th>
                <div className={style.sortColumnWrapper}>
                  <span className={style.ellipsisTextThreeLines}>{t('Deactivation period')}</span>
                </div>
              </th>
              <th />
            </tr>
          </thead>
          <tbody>
            {equipmentsListIsLoading ? (
              <TableEquipmentsSkeleton />
            ) : tableData.length ? (
              tableData.map((row) => (
                <tr
                  key={row.id}
                  className={cx(style.equipmentTableBodyRow, { [style.selectedRow]: selectedRows[row.id] })}
                >
                  <td>
                    <Checkbox
                      value={selectedRows[row.id]}
                      onChange={() => onChangeRowCheckbox(row)}
                      disabled={!isManageEquipmentDeactivation}
                      withHelperText={false}
                    />
                  </td>
                  <td className={style.locationColumn}>
                    <span className={style.ellipsisTextThreeLines}>
                      {translateField({ item: row.locationTable })}
                    </span>
                    {(locationSelectValue.id === 'all' || locationSelectValue.level_name === 'group') && (
                      <span
                        className={cx(style.ellipsisTextThreeLines, style.facilityName)}
                        data-tooltip-content={row.facility_name}
                        data-tooltip-id="cell-tooltip"
                      >
                        {row.facility_name}
                      </span>
                    )}
                  </td>
                  <td>
                    <span
                      className={style.ellipsisTextThreeLines}
                      data-tooltip-content={translateField({ item: row })}
                      data-tooltip-id="cell-tooltip"
                    >
                      {translateField({ item: row })}
                    </span>
                  </td>
                  <td className={style.assetCodeColumn}>
                    <span
                      className={style.ellipsisTextThreeLines}
                      data-tooltip-content={translateField({ item: row, field: 'asset_code' })}
                      data-tooltip-id="cell-tooltip"
                    >
                      {translateField({ item: row, field: 'asset_code' })}
                    </span>
                  </td>
                  <td className={style.equipmentTypeColumn}>
                    <span
                      className={style.ellipsisTextThreeLines}
                      data-tooltip-content={row.type?.name || null}
                      data-tooltip-id="cell-tooltip"
                    >
                      {row.type?.name || null}
                    </span>
                  </td>
                  <td className={style.countSensorsColumn}>{row.count_sensors}</td>
                  <td>
                    <div
                      className={
                        row.is_deactivated ? style.equipmentStatusInactive : style.equipmentStatusActive
                      }
                    >
                      {t(row.is_deactivated ? 'inactive' : 'active')}
                    </div>
                  </td>
                  <td>
                    <DeactivationPeriodColumn
                      deactivateTable={row.deactivateTable}
                      isDeactivated={!!row.is_deactivated}
                      dateFormat={`${profileData?.dateFormat.date} ${profileData?.dateFormat.time}`}
                      facilityOffset={usersTimezone}
                    />
                  </td>
                  <td>
                    <div
                      className={style.actionsIconContainer}
                      data-tooltip-id="table-equipments-action-menu"
                      data-tooltip-content={JSON.stringify(row)}
                    >
                      <ThreeDotsIcon />
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <TableEmptyRowExtended text={t('No equipments were found.')} />
            )}
          </tbody>
          <tfoot>
            <TableLoadingTrigger show={!equipmentsListIsFetching} ref={infiniteScrollRef} />
            <TableLoading show={equipmentsListIsFetching && !equipmentsListIsLoading} />
          </tfoot>
        </table>
        <TooltipSort id="sort-tooltip" sorting={sorting} />
        <Tooltip id="cell-tooltip" place="top-start" />
        <Tooltip id="checkbox-tooltip" place="top-end" />
        <ContextMenu
          id="table-equipments-action-menu"
          isManageEquipmentDeactivation={isManageEquipmentDeactivation}
        />
      </div>
      <div className={style.equipmentTableFooter}>
        <span className={style.equipmentTableFooterLeft}>
          {`${t('Displaying')} ${tableData.length} ${t('of')} ${totalByStatus[equipmentStatusValue]} ${t(
            'Equipments',
          )}`}
        </span>
        <div className={style.equipmentTableFooterRight}>
          {activeEquipment.length || inactiveEquipment.length ? (
            <>
              {equipmentStatusValue !== 'inactive' && (
                <div className={style.selectedRows}>
                  <span className={style.selectedRowsInfo}>
                    {activeEquipment.length} {t('active')}{' '}
                    {t(`item${activeEquipment.length > 1 ? 's' : ''} selected`)}
                  </span>
                  <Button
                    text={t('Deactivate')}
                    type="primary"
                    variant={'medium'}
                    onClick={handleDeactivateEquipment}
                    disabled={!activeEquipment.length}
                  />
                </div>
              )}
              {equipmentStatusValue !== 'active' && (
                <div className={style.selectedRows}>
                  <span className={style.selectedRowsInfo}>
                    {inactiveEquipment.length} {t('inactive')}{' '}
                    {t(`item${inactiveEquipment.length > 1 ? 's' : ''} selected`)}
                  </span>
                  <Button
                    text={t('Activate')}
                    type="primary"
                    variant={'medium'}
                    onClick={handleActivateEquipment}
                    disabled={!inactiveEquipment.length}
                  />
                </div>
              )}
              {equipmentStatusValue !== 'active' && (
                <div className={style.selectedRows}>
                  <span className={style.selectedRowsInfo}>
                    {inactiveEquipment.length} {t('inactive')}{' '}
                    {t(`item${inactiveEquipment.length > 1 ? 's' : ''} selected`)}
                  </span>
                  <Button
                    text={t('Edit Deactivation Period')}
                    type="primary"
                    variant={'medium'}
                    onClick={handleEditDeactivationPeriodEquipment}
                    disabled={!inactiveEquipment.length}
                  />
                </div>
              )}
            </>
          ) : null}
          <ButtonGoUp ref={equipmentTableContainerRef} />
        </div>
      </div>
    </div>
  );
};

export default TableEquipments;
